"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
	useActionState,
	useEffect,
	useId,
	useState,
	useSyncExternalStore,
} from "react";
import { signInAction } from "@/lib/actions/auth";
import {
	getAuthHashErrorMessage,
	parseAuthHashError,
} from "@/lib/auth/parse-auth-hash-error";
import { hashContainsAuthSession } from "@/lib/auth/parse-auth-hash-session";
import "./landing.css";

function subscribeHash(onStoreChange: () => void) {
	window.addEventListener("hashchange", onStoreChange);
	return () => window.removeEventListener("hashchange", onStoreChange);
}

function getHashSnapshot() {
	return typeof window !== "undefined" ? window.location.hash : "";
}

function getServerHashSnapshot() {
	return "";
}

/**
 * Log in on the landing photograph: the same corners as the landing page,
 * with email and password as two underlined lines in the lower right.
 */
export function LandingLogin() {
	const searchParams = useSearchParams();
	const hash = useSyncExternalStore(
		subscribeHash,
		getHashSnapshot,
		getServerHashSnapshot,
	);
	const isEstablishingSession = hashContainsAuthSession(hash);
	const [state, formAction, pending] = useActionState(signInAction, {
		success: false,
		error: "",
	});
	const [hashError, setHashError] = useState<string | null>(null);
	const emailId = useId();
	const passwordId = useId();

	// Errors from invite and magic links arrive in the URL hash.
	useEffect(() => {
		const parsed = parseAuthHashError(window.location.hash);
		if (!parsed) return;
		const message = getAuthHashErrorMessage(parsed);
		setHashError(`${message.title}. ${message.body}`);
		const { pathname, search } = window.location;
		window.history.replaceState(null, "", `${pathname}${search}`);
	}, []);

	const redirectTo = searchParams.get("redirectedFrom") || "/";
	const isVerified = searchParams.get("verified") === "true";
	const notice = state.error
		? state.error
		: hashError
			? hashError
			: isVerified
				? "Email verified. You can log in now."
				: null;

	return (
		<div className="ed-landing">
			<img
				src="/images/landing/lift-wall.jpg"
				alt="Two people writing on a lift wall"
				className="ed-landing__image"
				fetchPriority="high"
			/>
			<header className="ed-landing__corner ed-landing__corner--tl">
				<Link href="/" className="ed-landing__wordmark">
					COLORS*STUDIOS
				</Link>
				<h1 className="ed-landing__line">
					{isEstablishingSession
						? "Signing you in…"
						: "Log in if you’ve been invited."}
				</h1>
			</header>
			<p className="ed-landing__corner ed-landing__corner--tc">
				The forum · By invitation
			</p>
			<Link href="/" className="ed-landing__corner ed-landing__corner--tr">
				Request an invite
			</Link>
			<p className="ed-landing__corner ed-landing__corner--bl">© COLORS</p>

			{isEstablishingSession ? null : (
				<form
					action={formAction}
					className="ed-landing__corner ed-landing__corner--br"
				>
					<input type="hidden" name="redirectTo" value={redirectTo} />
					{notice ? (
						<span
							className="ed-landing__small"
							role={state.error || hashError ? "alert" : "status"}
						>
							{notice}
						</span>
					) : null}
					<span className="ed-landing__field">
						<label htmlFor={emailId} className="sr-only">
							Email address
						</label>
						<input
							id={emailId}
							type="email"
							name="email"
							required
							autoComplete="email"
							placeholder="Email"
							disabled={pending}
						/>
					</span>
					<span className="ed-landing__field">
						<label htmlFor={passwordId} className="sr-only">
							Password
						</label>
						<input
							id={passwordId}
							type="password"
							name="password"
							required
							autoComplete="current-password"
							placeholder="Password"
							disabled={pending}
						/>
						<button type="submit" disabled={pending}>
							{pending ? "Logging in…" : "Log in"}
						</button>
					</span>
					<Link href="/forgot-password" className="ed-landing__small">
						Forgot password?
					</Link>
				</form>
			)}
		</div>
	);
}
